<script setup>
import { useAppStore } from '@/stores/App'
import { useDisplay } from 'vuetify'

const nuxtApp = useNuxtApp()
const app = useAppStore()

const { smAndDown } = useDisplay()
const staticURLImg = useRuntimeConfig().public.staticURLImg

const searchText = ref()
const selectedCategory = ref()
const selectedSort = ref()

const { data, status, error } = await useLazyAsyncData('products', () => $fetch('/api/product/'), {
    transform: (data) => {
        return {
            products: data.products.edges.map((edge) => edge.node),
            fetchedAt: new Date(Date.now()),
        }
    },
    getCachedData(key) {
        const data = nuxtApp.payload.data[key] || nuxtApp.static.data[key]

        if (!data) {
            return
        }

        const expirationDate = new Date(data.fetchedAt)
        expirationDate.setTime(expirationDate.getTime() + 60 * 60000) // cache set to 1 hour
        const isExpired = expirationDate.getTime() < Date.now()
        if (isExpired) {
            return
        }

        return data
    },
})

watchEffect(() => {
    if (error.value) {
        throw createError({
            statusCode: 500,
            message: error.value,
            fatal: false,
        })
    }
})

// console.log('data ', data.value.products)

// Category list for filtering
const categories = computed(() => {
    return [...new Set(data.value.products.map((value) => value.category.name))]
})

// Filter products by search text, category or sort optios
const products = computed(() => {
    let nodes = data.value.products
    let tempArr = nodes

    // Filter by selected category
    if (selectedCategory.value != null) {
        tempArr = nodes.filter((e) => {
            return e.category.name.toLowerCase() === selectedCategory.value.toLowerCase()
        })
    }

    // Filter by search text
    if (searchText.value != null && searchText.value.length > 0) {
        tempArr = nodes.filter((e) => {
            return (e.name + '' + e.tagline + '' + e.seller.displayName + '' + e.keywords)
                .toLowerCase()
                .includes(searchText.value.toLowerCase())
        })
    }

    // Filter by selected dropdown option
    if (selectedSort.value != null) {
        switch (selectedSort.value) {
            case 'Name: A to Z':
                tempArr.sort(function (a, b) {
                    if (a.name < b.name) {
                        return -1
                    }
                    if (a.name > b.name) {
                        return 1
                    }
                    return 0
                })
                break
            case 'Name: Z to A':
                tempArr.sort(function (a, b) {
                    if (a.name < b.name) {
                        return 1
                    }
                    if (a.name > b.name) {
                        return -1
                    }
                    return 0
                })
                //.reverse()
                break
            case 'Price: Low to High':
                tempArr.sort(function (a, b) {
                    let aPrice
                    let bPrice
                    if (a.productPrices.totalCount > 0) {
                        aPrice = a.productPrices.edges[0].node.price
                    } else {
                        aPrice = a.price
                    }
                    if (b.productPrices.totalCount > 0) {
                        bPrice = b.productPrices.edges[0].node.price
                    } else {
                        bPrice = b.price
                    }
                    return aPrice - bPrice
                })
                break
            case 'Price: High to Low':
                tempArr.sort(function (a, b) {
                    let aPrice
                    let bPrice
                    if (a.productPrices.totalCount > 0) {
                        aPrice = a.productPrices.edges[0].node.price
                    } else {
                        aPrice = a.price
                    }
                    if (b.productPrices.totalCount > 0) {
                        bPrice = b.productPrices.edges[0].node.price
                    } else {
                        bPrice = b.price
                    }
                    return bPrice - aPrice
                })
                break
            default:
                break
        }
    }
    return tempArr
})

useSeoMeta({
    title: 'Shop',
    ogTitle: 'Shop',
    description: 'Shop for music production and graphic design software products.',
    ogDescription: 'Shop for music production and graphic design software products.',
})
</script>

<template>
    <v-row id="shop" justify="center" class="mt-1 slide-from-top">
        <v-col v-if="status === 'pending' || status === 'idle'" cols="10"><app-loader /></v-col>
        <v-col v-else="products" cols="12" sm="10" class="mb-16">
            <lazy-ad-body spot="center" page="shop" position="top" class="mb-8" />
            <v-row justify="end" align="start" class="mb-2 rounded">
                <v-col cols="12" sm="12" lg="4">
                    <app-field-search
                        v-model="searchText"
                        variant="outlined"
                        density="comfortable"
                        prepend-inner-icon="mdi-magnify"
                        placeholder="Start typing to search..."
                        hide-details="auto"
                    />
                </v-col>
                <v-col cols="12" sm="6" lg="4">
                    <app-field-select
                        v-model="selectedCategory"
                        :items="categories"
                        variant="outlined"
                        label="Filter..."
                        density="comfortable"
                        prepend-inner-icon="mdi-filter-variant"
                        hide-details="auto"
                    />
                </v-col>
                <v-col cols="12" sm="6" lg="4">
                    <app-field-select
                        v-model="selectedSort"
                        :items="app.productsSortOptions"
                        variant="outlined"
                        label="Sort..."
                        density="comfortable"
                        prepend-inner-icon="mdi-sort"
                        hide-details="auto"
                    />
                </v-col>
            </v-row>
            <v-row class="justify-center">
                <v-col v-for="product in products" :key="product.id" cols="12" sm="6" md="4" lg="3">
                    <product-card :product="product" />
                </v-col>
            </v-row>
        </v-col>
        <v-col cols="12" sm="10" class="fade-in-up">
            <ad-disclosure />
        </v-col>
    </v-row>
</template>

<style scoped></style>
